import { t } from 'state/intl/translations';
import { SignUpFieldsVariations } from 'state/launchdarkly/variations';
import { Region } from 'utils/environment/types';
import {
  COUNTRY_BASED_ZIP_CODE_REGEX,
  getPhoneNumberErrorMessage,
  isEmailValid,
  isNameInputValid,
  isPhoneNumberValid,
} from 'utils/form';
import { ISOs } from 'utils/form/constants';
import { ISignupErrors, ISignupForm } from 'utils/form/types';
import { validationConfig } from 'utils/form/user-profile-validation-config/validation';

import { getDobErrors, getLegacyDobErrors } from './dobValidation';
import { IOverrides, OverridableFields } from './types';

export { prepareErrorKeys } from './prepare-error-keys';
export { generateDisplayedErrors } from './generate-displayed-errors';

export const validateSignupFormError = ({
  region,
  overrides,
  signUpFieldsVariations,
  simpleValidation,
}: {
  region: Region;
  overrides?: IOverrides;
  signUpFieldsVariations: SignUpFieldsVariations;
  simpleValidation?: boolean;
}) => async (values: ISignupForm) => {
  const errors: ISignupErrors = {};
  const isRequired = (
    required: boolean | { readonly locales: readonly string[] },
    field?: OverridableFields
  ) => {
    if (field && overrides?.[field]) {
      return overrides[field].required;
    }
    return required === true || (required !== false && required.locales.includes(region));
  };

  if (signUpFieldsVariations.agreesToTermsOfService && !values.agreesToTermsOfService) {
    errors.agreesToTermsOfService = t('agreeToTermsOfServiceError');
  }
  if (signUpFieldsVariations.name) {
    if (!values.name.trim()) {
      errors.name = t('nameRequiredError');
    } else if (!isNameInputValid(values.name)) {
      errors.name = t('notValidNameError');
    }
  }

  if (values.email === '') {
    errors.email = t('emailRequiredError');
  } else if (!isEmailValid(values.email.trim())) {
    errors.email = t('notValidEmailError');
  }

  if (signUpFieldsVariations.favoriteStoreSelector) {
    if (!values.favoriteLocation?.storeId || !values.favoriteLocation?.storeNumber) {
      errors.favoriteLocation = t('favoriteLocationRequiredError');
    }
  }

  const selectedCountryRegion = values.country === ISOs.CAN ? Region.CA : Region.US;

  const phoneNumberValidation =
    isRequired(validationConfig.phoneNumber.required, OverridableFields.PHONE) || values.phoneNumber
      ? await isPhoneNumberValid({
          phoneNumber: values.phoneNumber,
          country: selectedCountryRegion,
          simpleValidation,
        })
      : { valid: true };

  if (!phoneNumberValidation.valid) {
    errors.phoneNumber = getPhoneNumberErrorMessage(phoneNumberValidation.error);
  }

  let dobError;
  if (typeof values.dob === 'object') {
    dobError = getDobErrors(values.dob, region) ?? undefined;
  } else {
    dobError = getLegacyDobErrors(values.dob) ?? undefined;
  }

  if (dobError) {
    errors.dob = dobError;
  }

  const zipCodeError = (() => {
    if (isRequired(validationConfig.zipcode.required) && !values.zipcode) {
      return values.country === ISOs.USA ? t('zipCodeRequiredError') : t('postalCodeRequiredError');
    }

    const regex =
      COUNTRY_BASED_ZIP_CODE_REGEX[values.country as keyof typeof COUNTRY_BASED_ZIP_CODE_REGEX];

    if (values.zipcode && values.country && regex && !regex.test(values.zipcode.toUpperCase())) {
      return values.country === 'USA' ? t('zipCodeInvalid') : t('postalCodeInvalid');
    }

    return undefined;
  })();

  if (zipCodeError) {
    errors.zipcode = zipCodeError;
  }

  return errors;
};
